// @ts-nocheck

import type { AirCfarPriceQuoteData } from "@b2bportal/air-cfar-api";
import type {
  AirChfarDiscount,
  AirChfarPriceQuoteData,
} from "@b2bportal/air-chfar-api";
import type { ArbitraryChargeProduct } from "@b2bportal/arbitrary-charge-api";
import {
  type IPriceLineData,
  type ISummaryLineData,
  PassengerType,
  SummaryLineType,
} from "@b2bportal/core-types";
import type { RewardsPrice } from "@b2bportal/core-utilities";
import { getPassengerType } from "@b2bportal/core-utilities";
import {
  OfferType,
  type WalletPayment,
  WalletPaymentType,
} from "@b2bportal/hopper-wallet-api";
import type { Fee } from "@b2bportal/hsbc-purchase-api";
import {
  Payment,
  Product,
  type ProductOpaqueValue,
  type QuotedProduct,
} from "@b2bportal/purchase-api";
import {
  cartQuoteSelectors,
  FlightSelectors,
  FlightPassengerSelectors,
  SeatSelectors,
  ParentState,
  getParentState,
  CardPaymentSelectors,
  AirPriceDropSelectors,
  AirCfarSelectors,
  AirDisruptionSelectorsV1,
} from "@hopper-b2b/checkout";
import type {
  ChoosePaymentProperties,
  ChooseTravelerProperties,
  Currency,
  DisruptionOfferQuote,
  FiatPrice,
  Prices,
  SessionInfo,
  TripCategory,
} from "@hopper-b2b/types";
import { ZERO_FIAT_AMOUNT } from "@hsbc/shared";
import type { FlightMachineContext } from "@hsbc/types";
import { amountToFiatPrice } from "@hsbc/utilities";
import { createSelector } from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { sum } from "lodash-es";
import type { State, StateValue } from "xstate";

type CheckoutState = State<FlightMachineContext>;
type CheckoutStateWithoutValue = Pick<CheckoutState, "context">;
export type CheckoutStateWithAndWithoutValue =
  | CheckoutState
  | CheckoutStateWithoutValue;

export const getIsPriceBreakdownLoading = (
  state: CheckoutStateWithAndWithoutValue
) => {
  const tripPricing = state.context.common.tripPricing;
  const quoteBreakdownPricing =
    FlightSelectors.getQuoteBreakdownFlightPricing(state);

  return quoteBreakdownPricing === undefined && tripPricing === undefined;
};

export const getSelectedTrip = (state: CheckoutStateWithAndWithoutValue) =>
  state.context.flightShop.selectedTrip;

export const getPricing = (state: CheckoutStateWithAndWithoutValue) => {
  const parentState = getParentState((state as CheckoutState).value);

  const shouldUseQuote = !(
    parentState === ParentState.contactInformation ||
    parentState === ParentState.passengerInformation
  );

  const tripPricing = state.context.common.tripPricing;

  if (!shouldUseQuote) return tripPricing;

  const quoteTripPricing =
    FlightSelectors.getQuoteBreakdownFlightPricing(state);
  const validPriceQuote =
    (quoteTripPricing?.pricingByPassenger?.length || 0) > 0;
  return validPriceQuote ? quoteTripPricing : tripPricing;
};
export const getShopPricingInfo = (state: CheckoutStateWithAndWithoutValue) =>
  state.context.flightShop.shopPricingInfo;

export const getSelectedTripDetails = (
  state: CheckoutStateWithAndWithoutValue
) => state.context.flightShop.tripDetails;

export const getCurrencyFromPricingInfo = (
  state: CheckoutStateWithAndWithoutValue
) => {
  const shopPricingInfo = getShopPricingInfo(state);
  return {
    currencyCode: shopPricingInfo.fare?.[0]?.pricing.baseAmount.fiat
      .currencyCode as Currency,
    currencySymbol:
      shopPricingInfo.fare?.[0]?.pricing.baseAmount.fiat.currencySymbol,
  };
};

export const getSellAtCurrency = (state: CheckoutStateWithAndWithoutValue) => {
  const sellAt = cartQuoteSelectors.getQuoteBreakdown(state)?.sellAt;
  return sellAt
    ? {
        currencyCode: sellAt.fiat.currencyCode as Currency,
        currencySymbol: sellAt.fiat.currencySymbol,
      }
    : null;
};

export const getCurrencyDifferenceFromQuotebreakdown = (
  state: CheckoutStateWithAndWithoutValue
) => {
  const balance = cartQuoteSelectors.getQuoteBreakdown(state)?.balance;
  const sellAt = cartQuoteSelectors.getQuoteBreakdown(state)?.sellAt;
  if (balance && sellAt) {
    if (balance.fiat.currencyCode !== sellAt.fiat.currencyCode) {
      return true;
    }
  }
  return false;
};

export const getProductsFromQuoteBreakdown = createSelector(
  cartQuoteSelectors.getQuoteBreakdown,
  (quoteBreakdown) => quoteBreakdown?.products ?? []
);

export const getFintechLineItemsFromQuoteBreakdown = createSelector(
  getProductsFromQuoteBreakdown,
  (products): FintechLineItem[] =>
    products
      .map(({ product }) => {
        switch (product.type) {
          case Product.AirCfar:
            return [
              {
                type: product.type,
                totalPremiumAmount: (product.value as AirCfarPriceQuoteData)
                  .totalPremiumAmount.fiat,
              },
            ];
          default:
            return [];
        }
      })
      .flat()
);

export const getAdditionalFeesLineItem = createSelector(
  getProductsFromQuoteBreakdown,
  (products): ProductLineItemType[] =>
    products
      .map(({ product }) => {
        switch (product.type) {
          case Product.Cart: {
            const { additionalNotes: notesStrings = "" } =
              product.value as ArbitraryChargeProduct;
            const { ratePerPax, name } = JSON.parse(notesStrings) as Fee;

            if (!ratePerPax || !name) return [];

            return {
              key: name,
              price: amountToFiatPrice(ratePerPax),
            };
          }
          default:
            return [];
        }
      })
      .flat()
);

interface FintechLineItem {
  type: Product;
  totalPremiumAmount: FiatPrice;
}

export const getProductTotalPrice = (
  state: CheckoutStateWithAndWithoutValue
): Prices => {
  return (
    state.context.cartQuote?.quoteBreakdown?.productTotal ?? {
      fiat: ZERO_FIAT_AMOUNT,
      accountSpecific: {},
    }
  );
};

export const getAfterQuoteTotalPrice = (
  state: CheckoutStateWithAndWithoutValue
): Prices => {
  const { accountSpecific, fiat } = getProductTotalPrice(state);
  const rewardsValues: Array<RewardsPrice> = Object.values(accountSpecific);

  return {
    fiat,
    rewards: rewardsValues?.[0] ?? { value: 0, currency: "points" },
  };
};

// TODO: fix below, this doesn't look right, quote product total already contains seats price
export const getFlightTotalInPricesWithoutCarrotCash = (
  state: CheckoutStateWithAndWithoutValue
) => {
  const parentState = getParentState((state as CheckoutState).value);

  const selectedSeats = SeatSelectors.getSelectedSeats(state);
  const tripPricing = getPricing(state);
  const currency = getCurrencyFromPricingInfo(state);
  const shopPricingInfo = getShopPricingInfo(state);

  const flightTotal = tripPricing?.totalPricing?.total.fiat.value;
  const farePricing = shopPricingInfo?.fare?.[0]?.pricing;
  const seatTotal = selectedSeats.reduce(
    (sum, seat) => sum + Number(seat.price),
    0
  );

  const shouldUseQuote = !(
    parentState === ParentState.contactInformation ||
    parentState === ParentState.passengerInformation
  );

  const productTotalPrice = getFlightBalance(state);

  const fintechProducts = getTripPriceLineItems(state)?.[0]?.products;
  const fintechTotal = fintechProducts
    ? Object.values(fintechProducts).reduce(
        (total, fiat) => (total += fiat.value),
        0
      )
    : 0;
  const voucherDiscount = getVoucherDiscount(state)?.fiat.value ?? 0;
  const promotionDiscount = getPromotionDiscount(state)?.fiat.value ?? 0;

  if (shouldUseQuote && productTotalPrice) {
    //Use checkout v1 breakdown value
    return {
      ...productTotalPrice,
      fiat: {
        ...productTotalPrice.fiat,
        //Seats cannot be removed from quote
        //FE shows a calculated price (total + seat) without adding seats to quote.
        value: productTotalPrice.fiat.value + seatTotal,
      },
    };
  } else if (flightTotal) {
    const fiatTotal =
      flightTotal +
      seatTotal +
      fintechTotal -
      voucherDiscount -
      promotionDiscount;

    return {
      fiat: {
        ...currency,
        value: fiatTotal < 0 ? 0 : fiatTotal,
      },
    };
  } else if (farePricing) {
    const fiatBaseAmount = farePricing.baseAmount.fiat.value || 0;
    const fiatTaxAmount = farePricing.taxAmount.fiat.value || 0;
    const fiatAdditionalMargin = farePricing.additionalMargin
      ? farePricing.additionalMargin.fiat.value
      : 0;

    const fiatTotal =
      fiatBaseAmount +
      fiatTaxAmount +
      fiatAdditionalMargin +
      seatTotal +
      fintechTotal -
      voucherDiscount -
      promotionDiscount;
    return {
      fiat: {
        currencyCode: farePricing.baseAmount.fiat.currencyCode,
        currencySymbol: farePricing.baseAmount.fiat.currencySymbol,
        value: fiatTotal < 0 ? 0 : fiatTotal,
      },
    };
  } else {
    return null;
  }
};

export const getVoucherDiscount = (
  state: CheckoutStateWithAndWithoutValue
): Prices => {
  if (state.context.cartQuote.quoteBreakdown) {
    const payment = state.context.cartQuote.quoteBreakdown?.payments.find(
      (p) =>
        p.payment.type === Payment.Wallet &&
        p.payment.value.paymentType === WalletPaymentType.Voucher
    );

    const discount = payment ? payment.payment.value : undefined;

    const isValueZero = discount?.totalDiscountAmount.fiat.value === 0;
    return isValueZero ? undefined : discount?.totalDiscountAmount;
  } else {
    const { offer } = state.context[ParentState.wallet];

    const isVoucher =
      offer?.offerType !== OfferType.Promotion &&
      offer?.offerType !== OfferType.Credit;

    if (offer && isVoucher) {
      const discount = {
        ...offer?.amount,
        fiat: {
          ...offer?.amount.fiat,
          value: Math.abs(offer.amount.fiat.value) || 0, // Wallet offers return values in negative
        },
      };
      const isValueZero = discount?.fiat.value === 0;
      return isValueZero ? undefined : discount;
    } else {
      return undefined;
    }
  }
};

export const getPromotionDiscount = (
  state: CheckoutStateWithAndWithoutValue
): Prices => {
  if (state.context.cartQuote.quoteBreakdown) {
    const payment = state.context.cartQuote.quoteBreakdown?.payments.find(
      (p) =>
        p.payment.type === Payment.Wallet &&
        p.payment.value.paymentType === WalletPaymentType.Promotion
    );

    const discount = payment ? payment.payment.value : undefined;

    const isValueZero = discount?.totalDiscountAmount.fiat.value === 0;
    return isValueZero ? undefined : discount?.totalDiscountAmount;
  } else {
    const { offer } = state.context[ParentState.wallet];

    if (offer && offer?.offerType === OfferType.Promotion) {
      const discount = {
        ...offer?.amount,
        fiat: {
          ...offer?.amount.fiat,
          value: Math.abs(offer.amount.fiat.value) || 0, // Wallet offers return values in negative
        },
      };
      const isValueZero = discount?.fiat.value === 0;
      return isValueZero ? undefined : discount;
    } else {
      return undefined;
    }
  }
};

export const getCarrotCash = (
  state: CheckoutStateWithAndWithoutValue
): Prices => {
  if (state.context.cartQuote.quoteBreakdown) {
    const payment = state.context.cartQuote.quoteBreakdown?.payments.find(
      (p) =>
        p.payment.type === Payment.Wallet &&
        (p.payment.value as WalletPayment).paymentType ===
          WalletPaymentType.Credit
    );
    const carrotCash = payment ? payment.payment.value : undefined;

    const isValueZero = carrotCash?.totalDiscountAmount.fiat.value === 0;
    return isValueZero ? undefined : carrotCash?.totalDiscountAmount;
  } else {
    const { creditOffer } = state.context[ParentState.wallet];

    // Max display carrot cash usage should not exceed the balance.
    // ex. Balance: $100, Carrot cash: $150, should display Carrot cash of $100.
    const calculateCreditUsageValue = () => {
      const flightTotal = getFlightTotalInPricesWithoutCarrotCash(state);
      if (flightTotal && creditOffer) {
        const value = flightTotal?.fiat.value + creditOffer?.amount.fiat.value;

        if (value < 0) {
          return Math.abs(creditOffer.amount.fiat.value - value); // Wallet offers return values in negative
        } else {
          return Math.abs(creditOffer.amount.fiat.value); // Wallet offers return values in negative
        }
      } else {
        return null;
      }
    };

    const credit = {
      ...creditOffer?.amount,
      fiat: {
        ...creditOffer?.amount.fiat,
        value: calculateCreditUsageValue() || 0,
      },
    };
    const isValueZero = credit?.fiat.value === 0;
    return isValueZero ? undefined : credit;
  }
};

export const getIsCreditPaymentAccounted = (
  state: CheckoutStateWithAndWithoutValue
): boolean => {
  return !!state.context.cartQuote.quoteBreakdown;
};

// This only return total price after quote currently. You may need to get total price from shop result for summary/breakdown before quote.
export const getFlightTotalInPrices = createSelector(
  getFlightTotalInPricesWithoutCarrotCash,
  getCarrotCash,
  getIsCreditPaymentAccounted,
  (flightsTotal, carrotCash, isCreditPaymentAccounted) => {
    if (flightsTotal) {
      const newTotal =
        flightsTotal.fiat.value -
        (isCreditPaymentAccounted ? 0 : (carrotCash?.fiat.value ?? 0));
      return {
        ...flightsTotal,
        fiat: {
          ...flightsTotal.fiat,
          value: newTotal < 0 ? 0 : newTotal,
        },
      };
    } else {
      return null;
    }
  }
);

export const getFlightBalance = (state: CheckoutStateWithAndWithoutValue) => {
  return state.context.cartQuote.quoteBreakdown?.balance;
};

export const getChfarDiscount = (state: CheckoutStateWithAndWithoutValue) => {
  const payment = state.context.cartQuote.quoteBreakdown?.payments.find(
    (p) => p.payment.type === Payment.AirChfarExerciseDiscount
  );

  const chfarDiscount = payment
    ? (payment.payment.value as AirChfarDiscount)
    : undefined;

  const isValueZero = chfarDiscount?.totalDiscountAmount.fiat.value === 0;
  return isValueZero ? undefined : chfarDiscount?.totalDiscountAmount;
};

type ProductLineItemType = { key: string; price: FiatPrice };

export type HsbcPriceLineData = IPriceLineData & {
  passengerType?: PassengerType;
};

const getProductLineItem = (
  product: ProductOpaqueValue
): ProductLineItemType[] => {
  switch (product.type) {
    case Product.AirCfar:
      return [
        {
          key: product.type,
          price: (product.value as AirCfarPriceQuoteData).totalPremiumAmount
            .fiat,
        },
      ];
    case Product.AirChfar:
      return [
        {
          key: product.type,
          price: (product.value as AirChfarPriceQuoteData).perPaxPremiumAmount
            .fiat,
        },
      ];
    case Product.AirDisruption: {
      const disruptionProducts = product.value.quotes as DisruptionOfferQuote[];
      return disruptionProducts.map(
        (disruptionProduct: DisruptionOfferQuote) => ({
          key: disruptionProduct.productType,
          price: disruptionProduct.pricePerPax.fiat,
        })
      );
    }
    case Product.AirPriceDrop:
      return [
        {
          key: product.type,
          price: ZERO_FIAT_AMOUNT,
        },
      ];
    default:
      return [];
  }
};

export const getProductLineItemsPerPaxPricing = (
  state: CheckoutStateWithAndWithoutValue
) => {
  const isPriceDropEligible =
    AirPriceDropSelectors.getPriceDropEligibility(state);
  // If cart quote has not been called use shop data
  if (cartQuoteSelectors.getQuoteBreakdown(state)) {
    const lineItems = cartQuoteSelectors
      .getQuoteBreakdown(state)
      ?.products?.map((quotedProduct: QuotedProduct) =>
        getProductLineItem(quotedProduct.product)
      )
      ?.flat()
      .reduce((acc, productLineItem: ProductLineItemType) => {
        acc[productLineItem.key] = productLineItem.price;
        return acc;
      }, {});

    if (isPriceDropEligible) {
      lineItems[Product.AirPriceDrop] = ZERO_FIAT_AMOUNT;
    }

    return lineItems;
  } else {
    const lineItems = {};
    const selectedDisruptionOfferQuote =
      AirDisruptionSelectorsV1.getSelectedDisruptionOfferQuote?.(state);

    if (isPriceDropEligible) {
      lineItems[Product.AirPriceDrop] = ZERO_FIAT_AMOUNT;
    }
    if (selectedDisruptionOfferQuote != null) {
      lineItems[DisruptionProductType.AirDisruptionProtection] =
        selectedDisruptionOfferQuote.quote.pricePerPaxLocalized?.fiat;
    }
    return lineItems;
  }
};

export const getTripPriceLineItems = (
  state: CheckoutStateWithAndWithoutValue
): HsbcPriceLineData[] => {
  const selectedSeats = SeatSelectors.getSelectedSeats(state);
  const tripPricing = getPricing(state);
  const shopPricingInfo = getShopPricingInfo(state);
  const productLineItemsPerPax = getProductLineItemsPerPaxPricing(state);
  const products = FlightSelectors.getQuoteBreakdownProducts(state);

  if (tripPricing?.pricingByPassenger?.length > 0) {
    return tripPricing?.pricingByPassenger?.map((pricing) => {
      const seatsProdudct = products.find(
        (p) => p.product.type === Product.Seats
      )?.product?.value as ItinerarySeats;

      let seatsPrice;
      // Sum pricing for all seats for each passenger, from seat map data
      if (seatsProdudct) {
        // data from quote breakdown
        seatsPrice = seatsProdudct.seats
          .filter((seat) => seat.personId === pricing.person.id)
          .reduce((sum, seat) => sum + seat.price, 0);
      } else {
        // data from seat map
        seatsPrice = selectedSeats.reduce(
          (sum, seat) =>
            seat.person_id === pricing.person.id
              ? sum + Number(seat.price)
              : sum,
          0
        );
      }

      const lineItems: HsbcPriceLineData = {
        person: pricing.person,
        passengerType: pricing.passengerType,
        baseAmount: pricing.baseWithoutMargin,
        taxesAndFees: pricing.taxes,
        products: [] as ProductLineData[],
        // TODO: we may move update quote for seats after seats picked, instead of before fulfillment,
        // so that we don't have to handle seats prices/line items separately, and avoid amount errors if seats fulfillment failed.
        total: {
          fiat: {
            ...pricing.total.fiat,
            value: pricing.total.fiat.value + seatsPrice,
          },
        },
      };

      // TODO: update once INF pax are added to the ancillaries
      if (pricing.passengerType !== "INF") {
        lineItems.products = [];
        if (productLineItemsPerPax?.length) {
          for (const productLineItem of productLineItemsPerPax) {
            lineItems.products.push(productLineItem);
          }
        }
        if (
          selectedSeats
            .map((seat) => seat.person_id)
            .includes(pricing.person.id)
        ) {
          lineItems.products.push({
            price: {
              fiat: { ...pricing.baseWithoutMargin.fiat, value: seatsPrice },
            },
            type: Product.Seats,
          });
        }
      }

      return lineItems;
    });
  } else if (shopPricingInfo.fare && shopPricingInfo.fare.length > 0) {
    const baseAmount = shopPricingInfo.fare[0].pricing.baseAmount;
    const taxesAndFees = shopPricingInfo.fare[0].pricing.taxAmount;

    const lineItems = [
      {
        person: undefined,
        baseAmount,
        taxesAndFees,
        products: { ...productLineItemsPerPax },
      },
    ];

    return lineItems;
  }
  return [];
};

export const getSelectedCardAccount = (
  state: CheckoutStateWithAndWithoutValue
) => {
  const selectedCardId = CardPaymentSelectors.getSelectedPaymentMethodId(
    state as any
  );

  if (!selectedCardId) return undefined;

  return CardPaymentSelectors.getPaymentMethods(state as any)?.find(
    (card) => card.id === selectedCardId
  );
};

export const getSummaryLineItems = (
  state: CheckoutStateWithAndWithoutValue
): ISummaryLineData[] => {
  // const flightTotalInPrices = getFlightTotalInPrices(state);
  const flightTotalInPrices = getProductTotalPrice(state);

  const lineItems: ISummaryLineData[] = [];
  if (flightTotalInPrices) {
    lineItems.push({
      type: SummaryLineType.TOTAL,
      price: flightTotalInPrices,
    });
  }

  return lineItems;
};

export const getFlightSearchDepartureLabel = (
  state: CheckoutStateWithAndWithoutValue
) => state.context.flightShop.departureLabel;

export const getFlightSearchReturnLabel = (
  state: CheckoutStateWithAndWithoutValue
) => state.context.flightShop.returnLabel;

export const getTripCategory = (state: CheckoutStateWithAndWithoutValue) =>
  state.context.flightSearch.tripCategory;

export const getSessionInfo = ({ context }: CheckoutStateWithAndWithoutValue) =>
  context.sessionInfo as unknown as SessionInfo;

export const getStateValue = ({ value }: { value: StateValue }) => value;

export const getClientAssets = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.clientAssets;

export const getFlightSearchDestination = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.destination;

export const getFlightSearchOrigin = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.origin;

export const getDestinationCity = ({ context }: CheckoutStateWithoutValue) =>
  context.flightShop.departureLabel;

export const getOriginCity = ({ context }: CheckoutStateWithoutValue) =>
  context.flightShop.returnLabel;

export const getFlightSearchDepartureDate = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.departureDate;

export const getFlightSearchReturnDate = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.returnDate;

export const getOriginCountryCode = ({
  context,
}: CheckoutStateWithAndWithoutValue) => context.flightSearch.originCountryCode;

export const getDestinationCountryCode = ({
  context,
}: CheckoutStateWithAndWithoutValue) =>
  context.flightSearch.destinationCountryCode;

export const getIsDomesticFlight = ({
  context,
}: CheckoutStateWithAndWithoutValue) => {
  return (
    context.flightSearch.originCountryCode ===
    context.flightSearch.destinationCountryCode
  );
};

export const getChooseTravelerProperties = ({
  context,
}: CheckoutStateWithAndWithoutValue): ChooseTravelerProperties => {
  const tripCategory = getTripCategory({ context }) as TripCategory;
  const destination = getFlightSearchDestination({ context });
  const origin = getFlightSearchOrigin({ context });
  const originCountryCode = getOriginCountryCode({ context });
  const destinationCountryCode = getDestinationCountryCode({ context });
  const departureDate = getFlightSearchDepartureDate({ context });
  const returnDate = getFlightSearchReturnDate({ context });
  const formattedDepartureDate = dayjs(departureDate).format("YYYY-MM-DD");
  const formattedReturnDate =
    returnDate && dayjs(returnDate).format("YYYY-MM-DD");

  return {
    departure_date: formattedDepartureDate,
    destination_country_code: destinationCountryCode,
    destination: `${destination?.id.code.regionType}/${destination?.id.code.code}`,
    origin_country_code: originCountryCode,
    origin: `${origin?.id.code.regionType}/${origin?.id.code.code}`,
    return_date: formattedReturnDate ? formattedReturnDate : undefined,
    trip_type: tripCategory,
  };
};

export const getChoosePaymentProperties = ({
  context,
}: CheckoutStateWithAndWithoutValue): ChoosePaymentProperties => {
  const chooseTravelerProperties = getChooseTravelerProperties({ context });
  const tripPricing = getPricing({ context });
  const totalSelectedPassengers =
    FlightPassengerSelectors.getAllSelectedPassengerIdsParent({
      context,
    }).length;
  const selectedInfants =
    FlightPassengerSelectors.getSelectedLapInfantIdsParent({
      context,
    }).length;
  const userPassengers = FlightPassengerSelectors.getUserPassengersParent({
    context,
  });
  const seatedPassengerIds =
    FlightPassengerSelectors.getSelectedPassengerIdsParent({
      context,
    });
  const selectedChildren = userPassengers.filter((pax) => {
    if (!seatedPassengerIds.includes(pax.id)) return false;
    return (
      getPassengerType({
        dateOfBirth: pax.dateOfBirth,
        tripDate: departureDate ?? undefined,
      }) === PassengerType.Child
    );
  }).length;
  const totalStops = sum(
    getSelectedTripDetails({ context }).slices.map((s) => s.stops)
  );

  return {
    ...chooseTravelerProperties,
    adult_price_total_usd: 0,
    base_total_usd: 0,
    seat_infant_price_total_usd: 0,
    price_per_pax_usd: 0,
    subtotal_usd: 0,
    total_price_usd: 0,
    charged_PQ_currency:
      tripPricing?.totalPricing.total.fiat.currencyCode || "USD",
    total_price: tripPricing?.totalPricing.total.fiat.value || 0,
    pax_total: totalSelectedPassengers,
    selected_pax_children: selectedChildren,
    selected_pax_lap_infants: selectedInfants,
    selected_pax_total: totalSelectedPassengers,
    total_stops: totalStops,
  };
};

const addFiatPrice = (total: FiatPrice, fiatPrice: FiatPrice): FiatPrice => {
  if (total.currencyCode !== fiatPrice.currencyCode) {
    throw Error("Trying to add prices of different currencies");
  } else {
    return {
      ...total,
      value: total.value + fiatPrice.value,
    };
  }
};

export const getFintechFiatTotal = (
  state: CheckoutStateWithAndWithoutValue
): FiatPrice => {
  let fiat: FiatPrice = ZERO_FIAT_AMOUNT;
  const selectedCfarOffer = AirCfarSelectors.getSelectedCfarOffer(state as any);
  if (selectedCfarOffer) {
    fiat = addFiatPrice(fiat, selectedCfarOffer.premiumAmount.fiat);
  }
  const selectedAirDisruptionOfferQuote =
    AirDisruptionSelectorsV1.getSelectedDisruptionOfferQuote?.(state);
  if (selectedAirDisruptionOfferQuote != null) {
    fiat = addFiatPrice(
      fiat,
      selectedAirDisruptionOfferQuote.quote.pricePerPaxLocalized?.fiat
    );
  }
  return fiat;
};

export const getSeatsFiatTotal = (
  state: CheckoutStateWithAndWithoutValue
): FiatPrice | null => {
  const selectedSeats = SeatSelectors.getSelectedSeats(state);
  if (selectedSeats?.length > 0) {
    const fiat = ZERO_FIAT_AMOUNT;
    const seatsTotal = selectedSeats.reduce((sum, seat) => {
      sum = sum + Number(seat.price);
      return sum;
    }, 0);
    return {
      ...fiat,
      value: fiat.value + seatsTotal,
    };
  } else {
    return null;
  }
};

/**
 * Counts in quote result as well as fintech, seats ancilliary pricing.
 *
 * Use cart quote breakdown product total when available, otherwise use total price from flight shop.
 *
 */
export const getFlightFiatTotal = (
  state: CheckoutStateWithAndWithoutValue
): FiatPrice => {
  let fiat = ZERO_FIAT_AMOUNT;
  const seatsTotal = getSeatsFiatTotal(state);
  const shopPricing = getShopPricingInfo(state);
  const tripPricing = getPricing(state);
  const fintechFiatTotal = getFintechFiatTotal(state);
  const quote = cartQuoteSelectors.getQuoteBreakdown(state);
  if (quote) {
    fiat = quote.balance.fiat;
  } else if (tripPricing?.pricingByPassenger?.length > 0) {
    fintechFiatTotal.value =
      fintechFiatTotal.value * tripPricing.pricingByPassenger.length;
    fiat = addFiatPrice(tripPricing.totalPricing.total.fiat, fintechFiatTotal);
  } else if (shopPricing?.fare?.length) {
    fiat = addFiatPrice(
      shopPricing.fare[0].pricing.total.fiat,
      fintechFiatTotal
    );
  }
  if (seatsTotal) {
    fiat = addFiatPrice(fiat, seatsTotal);
  }
  return fiat;
};

export const getAmountDueGivenTotalPrice = (
  state: CheckoutStateWithAndWithoutValue
) => {
  return (totalPrice: FiatPrice) => {
    const cashAwardsAmount =
      state.context.awardsPayment.rewardsAmountToPayInCash.value;
    const creditAmount = state.context.walletPayment.creditPaymentAmount.value;

    const amountDue =
      Math.round((totalPrice.value - cashAwardsAmount - creditAmount) * 100) /
      100;
    return amountDue > 0 ? amountDue : 0;
  };
};

/**
 * Amount to pay after travel credits, awards points. in the other words, amount to pay with card.
 */
export const getAmountDue = (state: CheckoutStateWithAndWithoutValue) => {
  const flightFiatTotal = getFlightFiatTotal(state);
  return getAmountDueGivenTotalPrice(state)(flightFiatTotal);
};

export const getRegisteredCardBins = (
  state: CheckoutStateWithAndWithoutValue
): string[] => {
  const cards = state.context.cardPayment?.paymentMethods;
  return cards ? cards.map((card) => card.bin) : [];
};
